import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslation } from 'hooks';
import { formatArrivalTime, pluralize, removeTimeOffset } from 'utils/utils';
import { Modal } from 'BookingFlow/components';
import { updatePreArrivalPreference } from 'store/preArrivalPreference';
import { arrivalDetailsUpdated } from 'store/analyticEvents';
import getInitialArrivalTime from 'utils/getInitialArrivalTime';
import ArrivalDetailCard from './ArrivalDetailCard';
import { ChangeETA, ChangeReasonForStay, ChangeGuestInfo } from './index';

const MODAL_VIEWS = {
	CHANGE_ETA: 'change-eta',
	CHANGE_REASON_FOR_STAY: 'change-reason-for-stay',
	CHANGE_GUEST_INFO: 'change-guest-info',
};

export default function ArrivalDetailCards({
	preArrivalPreferences,
	emptyGuestFields,
	stayReasons,
	stayReasonsAllOptions,
	upcomingTrip,
	propertyContent,
	profile,
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [changeDetailModal, setChangeDetailModal] = useState(null);

	const guestEta = preArrivalPreferences?.guestEta
		? removeTimeOffset(preArrivalPreferences?.guestEta, 'HH:mm:ss.SSS')
		: getInitialArrivalTime(propertyContent)(upcomingTrip);

	const numberOfAdults = upcomingTrip?.hotelProducts[0]?.guestCount?.numberAdults;
	const numberOfMinors = preArrivalPreferences?.guests.filter(guest => guest.isMinor).length;

	return (
		<>
			{changeDetailModal && (
				<Modal className="new-modal--change-detail" closeModal={() => setChangeDetailModal(null)}>
					{changeDetailModal === MODAL_VIEWS.CHANGE_ETA && (
						<ChangeETA
							arrivalTimeValue={guestEta}
							startDate={upcomingTrip.startDate}
							changeArrivalTime={({ arrivalTime }) => {
								dispatch(
									updatePreArrivalPreference({
										guestEta: arrivalTime,
										bookingId: upcomingTrip.bookingId,
									})
								);
								dispatch(
									arrivalDetailsUpdated({
										booking: upcomingTrip,
										arrivalDetailsValues: { guestEta: arrivalTime },
										profile,
									})
								);
							}}
							goBackHandler={() => setChangeDetailModal(null)}
						/>
					)}
					{changeDetailModal === MODAL_VIEWS.CHANGE_REASON_FOR_STAY && (
						<ChangeReasonForStay
							stayReasons={stayReasons}
							preArrivalPreferences={preArrivalPreferences}
							updateStayReasons={({ stayReasonIds, comments }) => {
								dispatch(
									updatePreArrivalPreference({
										stayReasonIds,
										comments,
										bookingId: upcomingTrip.bookingId,
									})
								);
								dispatch(
									arrivalDetailsUpdated({
										booking: upcomingTrip,
										arrivalDetailsValues: {
											stayReasonIds,
											...(comments !== undefined ? { comments } : {}),
										},
										stayReasonsAllOptions,
										profile,
									})
								);
							}}
							goBackHandler={() => setChangeDetailModal(null)}
						/>
					)}
					{changeDetailModal === MODAL_VIEWS.CHANGE_GUEST_INFO && (
						<ChangeGuestInfo
							guestInfoValues={[...(preArrivalPreferences?.guests || []), ...emptyGuestFields]}
							editGuestInfo={guestInfo => {
								const filteredGuestInfo = guestInfo.filter(
									({ firstName, isMinor, birthYear }) => firstName && (!isMinor || birthYear)
								);
								dispatch(
									updatePreArrivalPreference({
										guests: filteredGuestInfo,
										bookingId: upcomingTrip.bookingId,
									})
								);
								dispatch(
									arrivalDetailsUpdated({
										booking: upcomingTrip,
										arrivalDetailsValues: { guests: filteredGuestInfo },
										profile,
									})
								);
							}}
							upcomingTrip={upcomingTrip}
							propertyContent={propertyContent}
							goBackHandler={() => setChangeDetailModal(null)}
						/>
					)}
				</Modal>
			)}

			<div className="fs-trip__arrival-details">
				<h3 className="fs-trip__arrival-details__cards-title ty-h3">{t('Arrival Details')}</h3>
				<div className="fs-trip__arrival-details__cards">
					<ArrivalDetailCard
						icon="/images/profiles_2018/flight-arrival.svg"
						name={t('Arrival Time')}
						value={formatArrivalTime(guestEta)}
						onClick={() => setChangeDetailModal(MODAL_VIEWS.CHANGE_ETA)}
					/>
					<ArrivalDetailCard
						icon="/images/profiles_2018/beach-icon.svg"
						name={t('Reason For Stay')}
						value={
							stayReasonsAllOptions
								.filter(({ id }) => preArrivalPreferences.stayReasonIds.includes(id))
								.map(({ displayName }) => displayName)
								.join(', ') || preArrivalPreferences?.comments
						}
						onClick={() => setChangeDetailModal(MODAL_VIEWS.CHANGE_REASON_FOR_STAY)}
					/>
					<ArrivalDetailCard
						icon="/images/profiles_2018/occupancy-icon.svg"
						name={t('Guest Info')}
						value={`${pluralize(t, numberOfAdults + numberOfMinors, 'Guest')}`}
						onClick={() => setChangeDetailModal(MODAL_VIEWS.CHANGE_GUEST_INFO)}
					/>
				</div>
			</div>
		</>
	);
}
